
import { useState, useEffect } from 'react';
import { supabase } from '@/integrations/supabase/client';
import { toast } from 'sonner';
import { usePhoneVerification } from './usePhoneVerification';

export interface VerificationMethod {
  type: 'email' | 'phone';
  value: string;
  required: boolean;
}

interface UseVerificationMethodsProps {
  methods: VerificationMethod[];
  onComplete: () => void;
  autoLogin?: boolean;
  requireAllVerified?: boolean;
}

export function useVerificationMethods({
  methods,
  onComplete,
  autoLogin = true,
  requireAllVerified = false
}: UseVerificationMethodsProps) {
  const [verifiedMethods, setVerifiedMethods] = useState<Record<string, boolean>>({});
  const [error, setError] = useState<string | null>(null);
  
  // Filter to only get email and phone methods
  const emailMethod = methods.find(m => m.type === 'email');
  const phoneMethod = methods.find(m => m.type === 'phone');
  
  // Use the phone verification hook with type 'sms' for new signup verification
  const {
    loading: phoneLoading,
    verifyOtp: verifyPhoneOtp,
    resendCode: resendPhoneOtp,
  } = usePhoneVerification({
    onVerificationComplete: () => {
      handleMethodVerified('phone');
    },
    verificationType: 'sms'
  });
  
  // Check if all required methods are verified
  useEffect(() => {
    const allRequiredVerified = methods
      .filter(method => method.required && requireAllVerified)
      .every(method => verifiedMethods[method.type]);
    
    const anyVerified = Object.keys(verifiedMethods).length > 0;
    
    // Allow proceeding if all required methods are verified or if we don't require all to be verified
    if ((requireAllVerified && allRequiredVerified && anyVerified) || 
        (!requireAllVerified && anyVerified)) {
      if (autoLogin) {
        handleAutoLogin();
      } else {
        onComplete();
      }
    }
  }, [verifiedMethods, methods, onComplete, autoLogin, requireAllVerified]);
  
  const handleMethodVerified = (type: 'email' | 'phone') => {
    console.log(`Method ${type} has been verified`);
    setVerifiedMethods(prev => ({
      ...prev,
      [type]: true
    }));
  };
  
  // Wrapper function to match expected signature
  const handleVerifyPhoneOtp = async (otp: string): Promise<void> => {
    try {
      if (phoneMethod?.value) {
        await verifyPhoneOtp(otp);
      }
    } catch (error) {
      // Error is already handled in the hook
    }
  };
  
  // Attempt to automatically log the user in after all verifications
  const handleAutoLogin = async () => {
    try {
      console.log("Attempting auto-login after verification");
      if (emailMethod?.value) {
        // We don't need to explicitly sign in here as the verification should have already
        // established the session if successful
        const { data, error } = await supabase.auth.getSession();
        
        if (error) {
          console.error("Session retrieval error:", error);
          toast.error("Verification complete but couldn't automatically log you in. Please sign in manually.");
          onComplete();
          return;
        }
        
        if (data.session) {
          toast.success("Verification complete! You're now signed in.");
          onComplete();
        } else {
          console.log("No session after verification, attempting magic link");
          const { error: magicLinkError } = await supabase.auth.signInWithOtp({
            email: emailMethod.value,
          });
          
          if (magicLinkError) {
            console.error("Auto-login error:", magicLinkError);
            toast.error("Verification complete but couldn't automatically log you in. Please sign in manually.");
          } else {
            toast.success("Verification complete! Check your email for a login link.");
          }
          onComplete();
        }
      }
    } catch (error) {
      console.error("Auto-login exception:", error);
      onComplete();
    }
  };

  return {
    error,
    setError,
    verifiedMethods,
    emailMethod,
    phoneMethod,
    phoneLoading,
    handleVerifyPhoneOtp,
    resendPhoneOtp,
    handleMethodVerified
  };
}
